"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { sendMessage } from "@/actions/chat";
import { chatKeys } from "@/features/chat/queries/keys";
import {
  type MusicAttachment,
  musicAttachmentInput,
} from "@/lib/music/attachment";

/**
 * Send a song to chats as a playable song card (message kind "song"). The
 * server re-reads the song from the catalog per send; a note, if any, follows
 * as its own text message — the card itself never carries a caption.
 */
export function useSendSongToChats() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      song,
      chatIds,
      comment,
    }: {
      song: MusicAttachment;
      chatIds: number[];
      comment?: string;
    }) => {
      const input = musicAttachmentInput(song);
      const results = await Promise.all(
        chatIds.map(async (chatId) => {
          const sent = await sendMessage({
            chatId,
            content: "",
            song: input,
            clientMessageId: crypto.randomUUID(),
          });
          if ("error" in sent && sent.error) return sent;
          if (comment) {
            await sendMessage({
              chatId,
              content: comment,
              clientMessageId: crypto.randomUUID(),
            });
          }
          return sent;
        }),
      );
      // Partial success is success, as with links.
      const errors = results.flatMap((result) =>
        "error" in result && result.error ? [result.error] : [],
      );
      if (errors.length === results.length)
        throw new Error(errors[0] ?? "Failed to send");
      return results;
    },
    onSettled: async (_result, _error, variables) => {
      await Promise.all([
        ...variables.chatIds.map((chatId) =>
          queryClient.invalidateQueries({
            queryKey: chatKeys.chatMessages(chatId),
          }),
        ),
        queryClient.invalidateQueries({ queryKey: chatKeys.userChats("all") }),
      ]);
    },
  });
}
